// Motion helpers (from DipField 1.0.0). GPL-3.0-or-later.
// One rAF clock for every animation, one kill-switch, one reduced-motion check.

const FX_KEY = "dfsuite.fx";
let enabled = true;
try { enabled = localStorage.getItem(FX_KEY) !== "0"; } catch { /* ignore */ }

export const prefersReduced = () =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/** Motion is on only when the user wants it and the OS does not ask for less. */
export const fxOn = () => enabled && !prefersReduced();

export function setFx(on: boolean) {
  enabled = on;
  document.documentElement.classList.toggle("fx-off", !on);
  try { localStorage.setItem(FX_KEY, on ? "1" : "0"); } catch { /* ignore */ }
}
export const fxEnabled = () => enabled;
document.documentElement.classList.toggle("fx-off", !enabled);

// ---------- easing ----------
export const easeOut = (t: number) => 1 - Math.pow(1 - t, 3);
export const easeInOut = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
export const easeOutBack = (t: number) => { const c = 1.3; return 1 + (c + 1) * Math.pow(t - 1, 3) + c * Math.pow(t - 1, 2); };
export const clamp01 = (t: number) => (t < 0 ? 0 : t > 1 ? 1 : t);

type Stop = () => void;
const running = new Map<string, Stop>();
/** Tweens being called back at once (motion off). */
const sync = new Set<string>();

/**
 * Tween 0 -> 1 over `ms`, calling `onFrame(eased, raw)` each frame and once more at 1.
 * Re-using a `key` cancels the tween it replaces, so sliders never stack frames.
 * With motion off the value jumps straight to 1 — callers need no branch of their own.
 */
export function tween(key: string, ms: number, onFrame: (v: number, raw: number) => void,
                      ease: (t: number) => number = easeOut, onDone?: () => void): Stop {
  running.get(key)?.();
  if (!fxOn() || ms <= 0) {
    // called back at once; a callback that starts the same tween again is not followed, so a
    // redraw that restarts its own animation cannot recurse without end
    if (sync.has(key)) return () => { /* nothing to cancel */ };
    sync.add(key);
    try { onFrame(1, 1); onDone?.(); } finally { sync.delete(key); }
    return () => { /* nothing to cancel */ };
  }
  const t0 = performance.now();
  let raf = 0, dead = false;
  const step = (now: number) => {
    if (dead) return;
    const raw = clamp01((now - t0) / ms);
    onFrame(ease(raw), raw);
    if (raw < 1) raf = requestAnimationFrame(step);
    else { running.delete(key); onDone?.(); }
  };
  raf = requestAnimationFrame(step);
  const stop = () => { dead = true; cancelAnimationFrame(raf); running.delete(key); };
  running.set(key, stop);
  return stop;
}

export const cancel = (key: string) => { running.get(key)?.(); };

/** Per-item progress inside a staggered reveal. `spread` 0 = all together, 1 = strict queue. */
export function stagger(t: number, i: number, n: number, spread = 0.55) {
  if (n <= 1 || spread <= 0) return t;
  const start = (i / n) * spread;
  return clamp01((t - start) / (1 - spread));
}

/** Count a number up in place; formatting stays the caller's business. */
export function countUp(el: HTMLElement, to: number, fmt: (v: number) => string, ms = 520) {
  const from = Number(el.dataset.v ?? ""), now = performance.now(), last = Number(el.dataset.t ?? 0);
  el.dataset.v = String(to); el.dataset.t = String(now);
  if (!isFinite(to)) { el.textContent = "–"; return; }
  // a value that is being driven continuously (slider, drag, sweep) is shown as it is —
  // a tween would only ever lag one step behind the hand
  if (!isFinite(from) || from === to || !fxOn() || now - last < 160) { cancel("count:" + el.id); el.textContent = fmt(to); return; }
  tween("count:" + (el.id || Math.random()), ms, (v) => { el.textContent = fmt(from + (to - from) * v); });
}
